import React, { useState } from 'react';
import { Button, Upload, Image, Typography, Space, message } from 'antd';
import { useAppTranslation } from '../../hooks/useAppTranslation';
import { DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import type { UploadProps } from 'antd';
import { BRANDING_IMAGE_MAX_BYTES } from '../../hooks/useBusinessBrandingAssets';

const { Text } = Typography;

interface BrandingImageUploadProps {
  label: string;
  imageUrl?: string;
  disabled?: boolean;
  hint?: string;
  maxPreviewHeight?: number;
  /** Défaut : limite branding (500 Ko). */
  maxBytes?: number;
  onUpload: (file: File) => Promise<void>;
  onRemove?: () => Promise<void>;
}

const BrandingImageUpload: React.FC<BrandingImageUploadProps> = ({
  label,
  imageUrl,
  disabled = false,
  hint = undefined,
  maxPreviewHeight = 80,
  maxBytes = BRANDING_IMAGE_MAX_BYTES,
  onUpload,
  onRemove,
}) => {
  const { t } = useAppTranslation(['common']);
  const resolvedHint = hint ?? t('branding.hintDefault');
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);

  const handleBeforeUpload: UploadProps['beforeUpload'] = async (file) => {
    if (file.size > maxBytes) {
      message.error(t('branding.maxSize'));
      return Upload.LIST_IGNORE;
    }
    setUploading(true);
    try {
      await onUpload(file);
      message.success(t('branding.updated', { label }));
    } catch (error: unknown) {
      const errMsg = error instanceof Error ? error.message : t('branding.uploadError');
      message.error(errMsg);
    } finally {
      setUploading(false);
    }
    return Upload.LIST_IGNORE;
  };

  const handleRemove = async () => {
    if (!onRemove) return;
    setRemoving(true);
    try {
      await onRemove();
      message.success(t('branding.deleted', { label }));
    } catch (error: unknown) {
      const errMsg = error instanceof Error ? error.message : t('branding.deleteError');
      message.error(errMsg);
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div style={{ marginBottom: 16 }}>
      <Text strong style={{ display: 'block', marginBottom: 8 }}>
        {label}
      </Text>
      {imageUrl ? (
        <Space direction="vertical" size="small">
          <Image
            src={imageUrl}
            alt={label}
            style={{ maxHeight: maxPreviewHeight, objectFit: 'contain' }}
            preview
          />
          {onRemove && (
            <Button
              danger
              size="small"
              icon={<DeleteOutlined />}
              loading={removing}
              disabled={disabled || uploading}
              onClick={handleRemove}
            >
              {t('actions.delete')}
            </Button>
          )}
        </Space>
      ) : (
        <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
          {t('branding.noImage')}
        </Text>
      )}
      <Upload
        accept="image/png,image/jpeg,image/webp"
        showUploadList={false}
        beforeUpload={handleBeforeUpload}
        disabled={disabled || uploading || removing}
      >
        <Button icon={<UploadOutlined />} loading={uploading} disabled={disabled || removing}>
          {imageUrl ? t('actions.replace') : t('actions.upload')}
        </Button>
      </Upload>
      <Text type="secondary" style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
        {resolvedHint}
      </Text>
    </div>
  );
};

export default BrandingImageUpload;
